import React from 'react';
import { Alert, Typography } from 'antd';

import { D3RadarChart, RadarThresholds } from '../../../../components/charts/D3RadarChart';
import { EmptyState, InlineLoading, SectionCard } from '../../../../components/shared';
import { useGeneticScores } from '../../../../hooks/genetic/useGeneticScores';
import {
	CategoryScoreMap,
	DietCategoryKey,
	ExerciseCategoryKey,
} from '../../../../types/geneCodes/GeneticScoringTypes';
import './wellnessSynopsis.css';

const { Text } = Typography;

const THRESHOLDS: RadarThresholds = { good: 70, moderate: 40 };
const RADAR_HEIGHT = 240;

// Shape comes from display_score (rescaled to this patient's own range, which is what makes the chart readable);
// colour and tooltip come from the absolute score, so a band never means "best-and-worst for you".
const RADAR_SHAPE_KEY = 'display_score';
const RADAR_BAND_KEY = 'score';

const DIET_CATEGORIES: Array<{ key: DietCategoryKey; label: string }> = [
	{ key: 'mediterranean', label: 'Mediterranean' },
	{ key: 'intermittent_fasting', label: 'Intermittent fasting' },
	{ key: 'low_carb', label: 'Low carb' },
	{ key: 'plant_based', label: 'Plant based' },
	{ key: 'carnivore', label: 'Carnivore' },
	{ key: 'western', label: 'Western' },
];

const EXERCISE_CATEGORIES: Array<{ key: ExerciseCategoryKey; label: string }> = [
	{ key: 'power_explosive', label: 'Power & explosive' },
	{ key: 'strength_hypertrophy', label: 'Strength & hypertrophy' },
	{ key: 'metabolic_conditioning', label: 'Metabolic conditioning' },
	{ key: 'aerobic_endurance', label: 'Aerobic endurance' },
	{ key: 'motor_skill', label: 'Motor skill' },
	{ key: 'flexibility_mobility', label: 'Flexibility & mobility' },
];

interface ScoreRow {
	key: string;
	label: string;
	score: number;
	display_score: number;
}

const bandColor = (score: number): string => {
	if (score >= THRESHOLDS.good) return 'var(--mg-success)';
	if (score >= THRESHOLDS.moderate) return 'var(--mg-warning)';
	return 'var(--mg-danger)';
};

const buildRows = (
	scores: CategoryScoreMap<string>,
	categories: Array<{ key: string; label: string }>,
): ScoreRow[] =>
	categories.reduce<ScoreRow[]>((rows, category) => {
		const entry = scores?.[category.key];
		if (!entry) return rows;
		// score is absolute genetic compatibility; display_score is that value rescaled to this patient's own min/max for readability.
		rows.push({
			key: category.key,
			label: category.label,
			score: Number(entry.score) || 0,
			display_score: Number(entry.display_score) || 0,
		});
		return rows;
	}, []);

interface WellnessPanelProps {
	title: string;
	subtitle: string;
	emptyTitle: string;
	rows: ScoreRow[];
	isLoading: boolean;
	isError: boolean;
}

const WellnessPanel: React.FC<WellnessPanelProps> = ({
	title,
	subtitle,
	emptyTitle,
	rows,
	isLoading,
	isError,
}) => {
	let body: React.ReactNode;

	if (isLoading) {
		body = <InlineLoading minHeight={RADAR_HEIGHT} />;
	} else if (isError) {
		body = (
			<div data-testid="wellness-panel-error">
				<Alert type="error" showIcon message="Scores unavailable" description="These scores could not be loaded." />
			</div>
		);
	} else if (!rows.length) {
		body = <EmptyState title={emptyTitle} description="No scoreable genotypes for this sample." />;
	} else {
		body = (
			<>
				<D3RadarChart
					data={rows}
					dataKey={RADAR_SHAPE_KEY}
					colorKey={RADAR_BAND_KEY}
					nameKey="label"
					thresholds={THRESHOLDS}
					max={100}
					height={RADAR_HEIGHT}
				/>
				<div className="wellness-synopsis__scores">
					{rows.map(row => (
						<div key={row.key} data-testid={`wellness-score-${row.key}`} className="wellness-synopsis__score">
							<span aria-hidden className="wellness-synopsis__dot" style={{ background: bandColor(row.score) }} />
							<span className="wellness-synopsis__score-label">{row.label}</span>
							<span className="wellness-synopsis__score-value">{row.score}</span>
						</div>
					))}
				</div>
			</>
		);
	}

	return (
		<SectionCard title={title} subtitle={subtitle}>
			{body}
		</SectionCard>
	);
};

const LEGEND_COLORS = ['var(--mg-success)', 'var(--mg-warning)', 'var(--mg-danger)'];

const Legend: React.FC = () => (
	<div data-testid="wellness-legend" className="wellness-synopsis__legend">
		{LEGEND_COLORS.map(color => (
			<span key={color} aria-hidden className="wellness-synopsis__dot" style={{ background: color }} />
		))}
		<span>Good / moderate / limited genetic compatibility</span>
	</div>
);

export interface WellnessSynopsisProps {
	sampleId: string;
}

export const WellnessSynopsis: React.FC<WellnessSynopsisProps> = ({ sampleId }) => {
	const { diet, exercise, isLoading, isError } = useGeneticScores(sampleId);

	const dietRows = buildRows(diet.scores, DIET_CATEGORIES);
	const exerciseRows = buildRows(exercise.scores, EXERCISE_CATEGORIES);
	const anyRadar = !isLoading && !isError && Boolean(dietRows.length || exerciseRows.length);

	return (
		<div data-testid="wellness-synopsis" className="wellness-synopsis">
			<WellnessPanel
				title="Diet compatibility"
				subtitle="Dietary patterns ranked by genotype"
				emptyTitle="No diet scores"
				rows={dietRows}
				isLoading={isLoading}
				isError={isError}
			/>

			<WellnessPanel
				title="Exercise profile"
				subtitle="Training styles ranked by genotype"
				emptyTitle="No exercise scores"
				rows={exerciseRows}
				isLoading={isLoading}
				isError={isError}
			/>

			{anyRadar && <Legend />}
		</div>
	);
};

export default WellnessSynopsis;
